import type { FC } from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Button, PlainTooltip } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";

const WizardFooter = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 17px 30px;
    background-color: ${colorsPalettes.carbon["0"]};
    border: 0;
    text-align: right;
    margin-right: 2px;
    margin-left: 2px;
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
`;

const CancelButton = styled(Button)`
    margin-right: 4px;
`;

const ButtonWrapper = styled.span`
    display: inline-block;
`;

interface IListFooterProps {
    onSave: () => void;
    saveButtonEnabled?: boolean;
    isSaving: boolean;

    /**
     * Tooltip text for the save button
     */
    saveButtonTooltipText?: string;

    /**
     * Indicates whether we want to show a delete button or not
     */
    showDeleteButton?: boolean;

    /**
     * Callback upon clicking delete button
     */
    onDelete?: () => void;

    /**
     * Indicates whether we're currently deleting an item or not
     */
    isDeleting?: boolean;

    /**
     * Indicates whether we should show the cancel button or not
     * @type {boolean}
     */
    showCancelButton?: boolean;

    /**
     * callback upon clicking cancel button
     */
    onCancel?: () => void;
}

export const ListFooter: FC<IListFooterProps> = (props) => {
    const {
        onSave,
        onDelete,
        onCancel,
        saveButtonEnabled,
        isSaving,
        saveButtonTooltipText = "",
        showDeleteButton = false,
        isDeleting = false,
        showCancelButton = false,
    } = props;

    const translate = useMemo(() => {
        return i18nFilter();
    }, []);

    const saveButtonText = !isSaving
        ? "customcategories.wizard.save.button"
        : "customcategories.wizard.save.button.loading";

    const deleteButtonText = !isDeleting
        ? "customcategories.wizard.delete.button"
        : "customcategories.wizard.delete.button.loading";

    const isLoading = isSaving || isDeleting;

    return (
        <WizardFooter>
            <div>
                {showCancelButton ? (
                    <CancelButton
                        dataAutomation="auto-create-cancel-button"
                        type="flat"
                        onClick={onCancel}
                    >
                        {translate("customcategories.wizard.cancel.button")}
                    </CancelButton>
                ) : null}

                <PlainTooltip
                    placement="top"
                    cssClass="plainTooltip-element"
                    tooltipContent={saveButtonTooltipText}
                    enabled={saveButtonTooltipText.length > 0}
                >
                    <ButtonWrapper>
                        <Button
                            dataAutomation="auto-create-save-button"
                            type="primary"
                            onClick={onSave}
                            isDisabled={!saveButtonEnabled}
                            isLoading={isLoading}
                        >
                            {translate(saveButtonText)}
                        </Button>
                    </ButtonWrapper>
                </PlainTooltip>
            </div>
            {showDeleteButton ? (
                <Button
                    dataAutomation="auto-create-delete-button"
                    type="flatWarning"
                    onClick={onDelete}
                    isDisabled={isLoading}
                    isLoading={isLoading}
                >
                    {translate(deleteButtonText)}
                </Button>
            ) : null}
        </WizardFooter>
    );
};
